import { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, X } from "lucide-react";
import "./dropdown.css";

import PropTypes from "prop-types";

export default function Dropdown({
  options,
  disabled = false,
  placeholder = "Select",
  multiple = false,
  value,
  onChange = () => {},
  shouldCloseOnSelect = () => {},
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  let selected = value || "";

  if (multiple) {
    selected = Array.isArray(value) ? value : [];
  }

  const handleSelect = (option) => {
    if (multiple) {
      let updated;
      if (selected.includes(option)) {
        updated = selected.filter((item) => item !== option);
      } else {
        updated = [...selected, option];
        }
        onChange(updated);
      if (shouldCloseOnSelect?.(option, updated)) {
        setOpen(false);
      }
    } else {
      onChange && onChange(option);
      setOpen(false);
    }
  };

  const removeTag = (option, e) => {
    e.stopPropagation();
    if (multiple) {
      const updated = selected.filter((item) => item !== option);
      onChange(updated);
    }
  };

  // ✅ Close on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const renderDropdownValue = () => {
    if (!multiple) {
      return (
        <span className="dropdown-selected">
          {selected || placeholder}
        </span>
      );
    }
  
    if (!selected.length) {
      return placeholder;
    }
  
    return selected.map((s, index) => (
      <span
        key={`${s}-${index}`}
        className="dropdown-tag d-flex align-center"
      >
        {s}
  
        <X
          size={14}
          className="tag-remove"
          onClick={(e) => removeTag(s, e)}
        />
      </span>
    ));
  };
  

  return (
    <div className={`dropdown ${disabled ? "disabled" : ""}`} ref={dropdownRef}>
      <div type="button" className="dropdown-toggle" onClick={() => !disabled &&  setOpen(!open)}>
        <div className="dropdown-value">
          {renderDropdownValue()}
        </div>

        <ChevronDown
          size={18}
          className={`dropdown-arrow ${open ? "open" : ""}`}
        />
      </div>

      {open && (
        <div className="dropdown-menu">
          {options.map((option) => {
            const active = multiple
              ? selected.includes(option)
              : selected === option;

            return (
              <div
                key={option}
                className={`dropdown-item ${active ? "active" : ""}`}
                onClick={() => handleSelect(option)}
              >
                <span>{option}</span>
                {active && <Check size={16} className="check-icon" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ✅ Prop Validation */
Dropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,

  placeholder: PropTypes.string,

  multiple: PropTypes.bool,

  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),

  onChange: PropTypes.func,

  shouldCloseOnSelect: PropTypes.func,
};